import React, { memo, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface ModelOption {
  l: string;
  v: string;
}

interface ModelConfigEditorProps {
  nodeId: string;
  model?: string;
  models: ModelOption[];
  onUpdate: (id: string, data: any) => void;
  layout?: 'horizontal' | 'vertical';
}

const ModelConfigEditorInner: React.FC<ModelConfigEditorProps> = ({
  nodeId,
  model,
  models,
  onUpdate,
  layout = 'horizontal',
}) => {
  // 选择窗点击弹出（对齐脚本节点底部面板下拉）：原 hover 式（group-hover 纯 CSS）鼠标掠过即弹出，改为点击切换；
  // 外点关闭走 capture 阶段 mousedown（面板根 onMouseDown stopPropagation 会阻断冒泡，capture 先于 React 合成事件）
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [menuOpen]);

  // 单选模型：选中即写回并收起菜单（脚本面板同款）
  const selectModel = (v: string) => {
    onUpdate(nodeId, { model: v });
    setMenuOpen(false);
  };

  if (layout === 'vertical') {
    return (
      <div className="flex items-center justify-between gap-4">
        <span className="text-[12px] text-white w-12 shrink-0">模型选择</span>
        <div className="relative" ref={menuRef} style={{ width: '300px' }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] border border-white/[0.10] cursor-pointer transition-colors text-[12px] font-medium text-white hover:bg-white/10"
          >
            <span className="flex-1 text-left">{models.find(m => m.v === model)?.l || 'Topaz 高清放大'}</span>
            <ChevronDown size={12} className="text-white/40" />
          </button>
          {menuOpen && (
            <div className="absolute top-full right-0 pt-2 w-full z-[200]">
              <div className="bg-[#262626] border border-white/10 rounded-xl shadow-xl overflow-hidden">
                {models.map(m => (
                  <div
                    key={m.v}
                    onClick={() => selectModel(m.v)}
                    className={`px-3 py-2 text-xs font-medium cursor-pointer hover:bg-white/10 ${model === m.v ? 'text-white bg-white/5' : 'text-white/60'}`}
                  >
                    {m.l}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white"
      >
        <span>{models.find(m => m.v === model)?.l || 'AI Model'}</span>
        <ChevronDown size={12} />
      </button>
      {menuOpen && (
        <div className="absolute bottom-full left-0 pb-2 w-40 z-[200]">
          <div className="bg-[#262626] border border-white/10 rounded-xl shadow-xl overflow-hidden">
            {models.map(m => (
              <div
                key={m.v}
                onClick={() => selectModel(m.v)}
                className={`px-3 py-2 text-xs font-medium cursor-pointer hover:bg-white/10 ${model === m.v ? 'text-white bg-white/5' : 'text-white/60'}`}
              >
                {m.l}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ModelConfigEditor = memo(ModelConfigEditorInner);
